import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import AuthShell from '../components/AuthShell';

const rowStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };

export default function Signup() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '', password: '', first_name: '', last_name: '',
    university: '', graduation_year: '', degree: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await api.post('/auth/signup', formData);
      login(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally { setLoading(false); }
  };

  return (
    <AuthShell>
      {/* Headline */}
      <div style={{ marginBottom: 26 }}>
        <h1 style={{
          fontFamily: 'var(--tandm-font-display)',
          fontSize: 30, fontWeight: 500, color: 'var(--tandm-on-dark)',
          letterSpacing: '-0.03em', margin: '0 0 5px', lineHeight: 1.15,
        }}>
          Join Tandm.<br />
          <span style={{
            background: 'var(--tandm-gradient-text)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>It's free.</span>
        </h1>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(185,28,28,0.14)', border: '1px solid rgba(252,165,165,0.3)',
          color: '#FCA5A5', borderRadius: 10, padding: '10px 14px',
          marginBottom: 18, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Form */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Name row */}
        <div style={rowStyle}>
          <div>
            <label className="lf-label">First name</label>
            <input name="first_name" type="text" required value={formData.first_name} onChange={handleChange} className="lf-input" placeholder="Jane" />
          </div>
          <div>
            <label className="lf-label">Last name</label>
            <input name="last_name" type="text" required value={formData.last_name} onChange={handleChange} className="lf-input" placeholder="Smith" />
          </div>
        </div>

        <div>
          <label className="lf-label">Email</label>
          <input name="email" type="email" required value={formData.email} onChange={handleChange} className="lf-input" placeholder="jane@university.ac.uk" />
        </div>

        <div>
          <label className="lf-label">Password</label>
          <div className="pw-wrap">
            <input
              name="password" type={showPassword ? 'text' : 'password'} required
              value={formData.password} onChange={handleChange}
              className="lf-input" placeholder="Minimum 8 characters" style={{ paddingRight: 42 }}
            />
            <button type="button" className="pw-btn" onClick={() => setShowPassword(s => !s)}>
              {showPassword
                ? <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                : <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              }
            </button>
          </div>
        </div>

        <div>
          <label className="lf-label">University</label>
          <input name="university" type="text" value={formData.university} onChange={handleChange} className="lf-input" placeholder="London School of Economics" />
        </div>

        <div style={rowStyle}>
          <div>
            <label className="lf-label">Degree</label>
            <input name="degree" type="text" value={formData.degree} onChange={handleChange} className="lf-input" placeholder="BSc Finance" />
          </div>
          <div>
            <label className="lf-label">Grad year</label>
            <input name="graduation_year" type="number" value={formData.graduation_year} onChange={handleChange} className="lf-input" placeholder="2026" />
          </div>
        </div>

        <button onClick={handleSubmit} disabled={loading} className="lf-btn" style={{ marginTop: 6 }}>
          {loading
            ? <><div className="lf-spinner" />Creating account...</>
            : <>Create free account <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg></>
          }
        </button>
      </div>

      {/* Legal */}
      <p style={{ textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 16, marginBottom: 0, lineHeight: 1.6 }}>
        By signing up you agree to our{' '}
        <span style={{ color: 'var(--tandm-accent-light)', opacity: 0.75, cursor: 'pointer' }}>Terms</span>
        {' '}&amp;{' '}
        <span style={{ color: 'var(--tandm-accent-light)', opacity: 0.75, cursor: 'pointer' }}>Privacy Policy</span>
      </p>

      {/* Footer link */}
      <p style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.3)', marginTop: 14, marginBottom: 0 }}>
        Already have an account?{' '}
        <Link to="/login" className="lf-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
